import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { useWebSocket } from '../hooks/useWebSocket'
import { useAuthStore } from '../stores/authStore'
import { useApi } from '../hooks/useApi'
import { ChatContext, type ChatContextValue, type ChatMessage, type OnlineMember } from './ChatContextBase'

interface ChatHistoryResponse {
  messages?: ChatMessage[]
}

interface OnlineMembersResponse {
  members?: OnlineMember[]
}

const MAX_MESSAGES = 200 // メモリ管理のため最大メッセージ数を制限

export function ChatProvider({ children }: { children: ReactNode }) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [onlineMembers, setOnlineMembers] = useState<OnlineMember[]>([])
  const [typingUsers, setTypingUsers] = useState(new Set<string>())
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const user = useAuthStore((state) => state.user)
  const api = useApi()
  const typingTimeoutRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const hasLoadedHistoryRef = useRef(false)

  // WebSocketメッセージハンドラー
  const handleWebSocketMessage = useCallback((message: Record<string, unknown>) => {
    try {
      // メッセージが存在しない場合はスキップ
      if (!message) {
        console.log('[ChatContext] メッセージが空')
        return
      }

      console.log('[ChatContext] WebSocketメッセージ受信:', message)

      // eventTypeが存在しない場合はスキップ
      if (!message.eventType) {
        console.log('[ChatContext] eventTypeが存在しません')
        return
      }

      const data = message.data as Record<string, unknown>

      switch (message.eventType) {
        case 'chat.message':
          console.log('[ChatContext] チャットメッセージ受信:', data)
          // 新しいメッセージを追加（重複チェック）
          setMessages(prev => {
            const chatMsg = data as unknown as ChatMessage
            // 既に存在するメッセージIDかチェック
            if (prev.some(msg => msg.messageId === chatMsg.messageId)) {
              console.log('[ChatContext] 重複メッセージをスキップ:', chatMsg.messageId)
              return prev
            }
            const newMessages = [...prev, chatMsg]
            console.log('[ChatContext] メッセージを追加。合計:', newMessages.length)
            if (newMessages.length > MAX_MESSAGES) {
              return newMessages.slice(-MAX_MESSAGES)
            }
            return newMessages
          })
          break

        case 'chat.typing': {
          // タイピング状態を更新
          const { userId, userName, isTyping } = data as { userId: string; userName: string; isTyping: boolean }
          setTypingUsers(prev => {
            const next = new Set(prev)
            const key = JSON.stringify({ userId, userName })

            if (isTyping) {
              next.add(key)

              // 既存のタイムアウトをクリア
              if (typingTimeoutRef.current[userId]) {
                clearTimeout(typingTimeoutRef.current[userId])
              }

              // 5秒後に自動的にタイピング状態を解除
              typingTimeoutRef.current[userId] = setTimeout(() => {
                setTypingUsers(current => {
                  const updated = new Set(current)
                  updated.delete(key)
                  return updated
                })
                delete typingTimeoutRef.current[userId]
              }, 5000)
            } else {
              next.delete(key)
              if (typingTimeoutRef.current[userId]) {
                clearTimeout(typingTimeoutRef.current[userId])
                delete typingTimeoutRef.current[userId]
              }
            }
            return next
          })
          break
        }

        case 'presence.update':
          // オンラインメンバーを更新
          setOnlineMembers((message as unknown as { items?: OnlineMember[] }).items || [])
          break
      }
    } catch (error) {
      console.error('[ChatContext] WebSocketメッセージ処理エラー:', error)
    }
  }, [])

  // WebSocket接続
  const { connected } = useWebSocket(handleWebSocketMessage)

  // メッセージ履歴を取得
  const fetchMessageHistory = useCallback(async () => {
    if (hasLoadedHistoryRef.current) return

    setIsLoadingHistory(true)
    try {
      const response = await api.get<ChatHistoryResponse>('/api/chat/messages?limit=100')
      setMessages(response.messages || [])
      hasLoadedHistoryRef.current = true
      console.log('[ChatContext] メッセージ履歴取得完了:', response.messages?.length || 0)
    } catch (error) {
      console.error('[ChatContext] メッセージ履歴取得エラー:', error)
    } finally {
      setIsLoadingHistory(false)
    }
  }, [api])

  // メッセージ送信
  const sendMessage = useCallback(async (message: string) => {
    if (!message || !message.trim()) {
      throw new Error('メッセージが空です')
    }

    try {
      await api.post('/api/chat/messages', { message: message.trim() })
    } catch (error) {
      console.error('[ChatContext] メッセージ送信エラー:', error)
      throw error
    }
  }, [api])

  // タイピング状態の更新
  const setTyping = useCallback(async (isTyping: boolean) => {
    try {
      await api.post('/api/chat/typing', { isTyping })
    } catch (error) {
      console.error('[ChatContext] タイピング状態更新エラー:', error)
    }
  }, [api])

  // オンラインメンバーを取得
  const fetchOnlineMembers = useCallback(async () => {
    try {
      console.log('[ChatContext] オンラインメンバー取得開始')
      const response = await api.get<OnlineMembersResponse>('/api/chat/online-members')
      console.log('[ChatContext] オンラインメンバー取得完了:', response)
      setOnlineMembers(response.members || [])
      console.log('[ChatContext] オンラインメンバー数:', response.members?.length || 0)
    } catch (error) {
      console.error('[ChatContext] オンラインメンバー取得エラー:', error)
    }
  }, [api])

  // 初回マウント時にメッセージ履歴とオンラインメンバーを取得
  useEffect(() => {
    if (user && connected) {
      fetchMessageHistory()
      fetchOnlineMembers()
    }
  }, [user, connected, fetchMessageHistory, fetchOnlineMembers])

  // クリーンアップ
  useEffect(() => {
    return () => {
      // すべてのタイピングタイムアウトをクリア
      Object.values(typingTimeoutRef.current).forEach(timeout => {
        clearTimeout(timeout)
      })
      typingTimeoutRef.current = {}
    }
  }, [])

  const value: ChatContextValue = {
    messages,
    onlineMembers,
    typingUsers,
    sendMessage,
    setTyping,
    connected,
    fetchOnlineMembers,
    fetchMessageHistory,
    isLoadingHistory,
  }

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  )
}
